import * as braze from '@braze/web-sdk';
import { Store } from 'redux';

import { selectIsUserLoggedIn } from '@bolteu/redux-session';

import config from '../config/index';
import { TargetingService } from './TargetingService';

export enum BrazeCustomEvent {
  MERCHANT_PORTAL_NAVIGATION = 'merchant_portal_navigation',
}

export class BrazeService {
  private inAppMessageSubscriptionId: string | undefined;

  constructor(private globalReduxStore: Store, private targetingService: TargetingService) {
    const { apiKey, apiUrl } = config.braze;
    if (apiKey && apiUrl) {
      this.init(apiKey, apiUrl);
    }
  }

  private init(apiKey: string, apiUrl: string) {
    const isInited = braze.initialize(apiKey, {
      baseUrl: apiUrl,
    });

    if (isInited) {
      if (this.isLoggedIn()) {
        this.subscribeToInAppMessages();
      }

      braze.openSession();

      // this sub is never removed, but so long as we expect a single BrazeService instance,
      // the footprint will not be significant.
      this.globalReduxStore.subscribe(() => {
        if (this.isLoggedIn()) {
          this.subscribeToInAppMessages();
        } else {
          this.unsubscribeFromInAppMessages();
        }
      });
    }
  }

  private isBrazeWebSdkEnabled(): boolean {
    return Boolean(
      this.targetingService.getParameters().delivery_provider_portal_flags?.enable_braze_web_sdk,
    );
  }

  private isLoggedIn(): boolean {
    return selectIsUserLoggedIn(this.globalReduxStore.getState());
  }

  private subscribeToInAppMessages() {
    if (this.inAppMessageSubscriptionId) {
      return;
    }

    this.inAppMessageSubscriptionId = braze.subscribeToInAppMessage((message) => {
      if (this.isLoggedIn()) {
        braze.showInAppMessage(message);
      }
    });
  }

  private unsubscribeFromInAppMessages() {
    if (!this.inAppMessageSubscriptionId) {
      return;
    }

    braze.removeSubscription(this.inAppMessageSubscriptionId);
    this.inAppMessageSubscriptionId = undefined;
  }

  changeUser(providerId: number) {
    if (braze.isInitialized()) {
      braze.changeUser(`delivery-provider-${providerId}`);
    }
  }

  setLanguage(language: string) {
    if (braze.isInitialized()) {
      braze
        .getUser()
        ?.setCustomUserAttribute('portal_language', language || window.navigator.language);
    }
  }

  setHasSponsoredListingsAccess(hasSponsoredListingsAccess: boolean) {
    if (braze.isInitialized()) {
      braze
        .getUser()
        ?.setCustomUserAttribute('has_sponsored_listings_access', hasSponsoredListingsAccess);
    }
  }

  logCustomEvent(eventName: BrazeCustomEvent, eventProperties?: Record<string, unknown>) {
    if (braze.isInitialized() && this.isBrazeWebSdkEnabled()) {
      braze.logCustomEvent(eventName, eventProperties);
    }
  }
}
